/*
Home block "what shipped. what's next.": the models released in the last two weeks, newest
first, and the studios most likely to release next (one estimate from the studio's rhythm
and the rumours about it, as on /calendar/releases/). A column with nothing to show says so;
with both empty, or when the radio does not answer, the block stays hidden. Needs
partials/releases-home.hbs on the page.
*/
import afterLoad from './afterLoad';
import startRequest from './latestRequest';
import { element } from './eventsData';
import { daysFromToday, fetchReleases, shortDate } from './releasesData';
import { chancePercent, chanceText, dateRange, studioMark } from './releasesView';

// A row of "expected next" is taller (it has a chance bar): seven releases fill the same
// height as six estimates, so the two cards end level.
const SHIPPED_ROWS = 7;
const NEXT_ROWS = 6;
const SHIPPED_DAYS = 14;

export default function releasesHome() {
    const root = document.querySelector('[data-releases-home]');
    if (!root) {
        return;
    }
    afterLoad(() => showReleases(root));
}

async function showReleases(root) {
    const isLatest = startRequest(root);
    let data;
    try {
        data = await fetchReleases();
    } catch (error) {
        if (isLatest()) {
            console.warn('[releases] calendar not loaded, home block hidden', error);
            root.hidden = true;
        }
        return;
    }
    if (!isLatest()) {
        return;
    }
    const studios = new Map(data.studios.map((studio) => [studio.id, studio]));
    const shipped = justShipped(data.releases, new Date());
    const next = expectedNext(data.studios);
    if (!shipped.length && !next.length) {
        root.hidden = true;
        return;
    }
    const shippedRows = shipped.map((release) => shippedRow(release, studios.get(release.studio)));
    root.querySelector('[data-releases-shipped]').replaceChildren(
        ...listRows(shippedRows, 'Nothing new in two weeks'),
    );
    root.querySelector('[data-releases-next]').replaceChildren(
        ...listRows(next.map(nextRow), 'No estimate yet'),
    );
    root.hidden = false;
}

/** Released in the last SHIPPED_DAYS days (today included), newest first. */
function justShipped(releases, now) {
    return releases
        .filter((release) => {
            const days = daysFromToday(release.date, now);
            return days <= 0 && days > -SHIPPED_DAYS;
        })
        .sort((a, b) => b.date.localeCompare(a.date) || a.name.localeCompare(b.name))
        .slice(0, SHIPPED_ROWS);
}

/** Studios with a chance estimate, most likely first. */
function expectedNext(studios) {
    return studios
        .filter((studio) => studio.expected && studio.expected.p30 !== null)
        .sort((a, b) => b.expected.p30 - a.expected.p30)
        .slice(0, NEXT_ROWS);
}

function listRows(rows, emptyText) {
    return rows.length ? rows : [element('li', 'th-releases-home__empty', emptyText)];
}

function shippedRow(release, studio) {
    const row = element('li', 'th-releases-home__row');
    const text = element('span', 'th-releases-home__text');
    text.append(
        element('span', 'th-releases-home__name', release.name),
        element('span', 'th-releases-home__sub', `${studio.name} · ${release.type}`),
    );
    row.append(studioMark(studio), text, dateCell(release.date, shortDate(release.date)));
    return row;
}

function nextRow(studio) {
    const { expected } = studio;
    const row = element('li', 'th-releases-home__row th-releases-home__row--next');
    const text = element('span', 'th-releases-home__text');
    const bar = element('span', 'th-releases-home__bar');
    bar.style.setProperty('--chance', `${chancePercent(expected.p30)}%`);
    text.append(
        element('span', 'th-releases-home__name', studio.name),
        element('span', 'th-releases-home__sub', chanceText(expected)),
        bar,
    );
    row.append(studioMark(studio), text, dateCell(expected.start, `~${dateRange(expected)}`));
    return row;
}

function dateCell(key, label) {
    const time = element('time', 'th-releases-home__when', label);
    time.dateTime = key;
    return time;
}
